import {weakMemo} from '@packages/utilities/memo';
import {extractOrigFileNameParts} from './extractOrigFileNameParts';

/**
 * Generates a unique key for a file based on its base name and extension.
 *
 * @param params - The parameters for generating the key.
 * @param params.baseName - The base name of the file.
 * @param params.extension - The extension of the file.
 * @returns A unique key combining the base name and extension.
 *
 * @example
 * const key = getOrigFileNameKey({ baseName: 'image', extension: '.jpg' });
 * // Returns: 'image.jpg'
 */
export const getOrigFileNameKey = ({
  baseName,
  extension,
}: {
  baseName: string;
  extension: string;
}) => `${baseName}${extension}`;

/**
 * Get assets by their original file name, ignoring numeric suffixes.
 *
 * This function creates a map where the keys are unique combinations of
 * base names and extensions, and the values are the count of assets with that combination.
 *
 * @param assets - An array of assets with original file names.
 * @returns A map of file name keys to their duplicates.
 *
 * @example
 * const origFileNames = [
 *   'image.jpg',
 *   'image (1).jpg',
 *   'other.jpg'
 * ];
 * // Returns: {
 * //   'image.jpg': [
 * //     'image.jpg',
 * //     'image (1).jpg'
 * //   ],
 * //   'other.jpg': [
 * //     'other.jpg'
 * //   ]
 * // }
 */
export const getDuplicateAssetsByOrigFileName = weakMemo(
  (origFileNames: string[]) => {
    const values: Record<string, string[]> = {};

    origFileNames.forEach((name) => {
      const {baseName, extension} = extractOrigFileNameParts(name);
      const key = getOrigFileNameKey({baseName, extension});
      if (!values[key]) {
        values[key] = [];
      }

      values[key].push(name);
    });

    return values;
  }
);
